import { useState, useEffect, useMemo } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { analyzeWorkShift } from "../utils/calculateWorkTime";
import { CHART_DATASETS, PRIMARY_COLORS } from "../config/chartColors";

/**
 * Хук для работы с иерархическими данными аналитики
 * @param {Object} options - Параметры для запроса
 * @param {Object} options.dateRange - Диапазон дат {start, end}
 * @param {string} options.groupBy - Уровень группировки (company, department, shift, employee)
 * @param {Array} options.departmentIds - ID выбранных отделов для фильтрации
 * @param {Array} options.shiftIds - ID выбранных смен для фильтрации
 * @param {Array} options.userIds - ID выбранных пользователей для фильтрации
 * @param {number} options.limit - Количество записей для отображения
 * @param {string} options.sortOrder - Порядок сортировки (asc, desc)
 * @param {string} options.sortBy - Поле для сортировки
 * @param {string} options.viewMode - Режим просмотра (top, bottom, all)
 * @returns {Object} - Состояние запроса и данные
 */
export const useHierarchicalAnalytics = (options) => {
    const {
        dateRange = {
            start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
            end: new Date(),
        },
        groupBy = "company",
        departmentIds = [],
        shiftIds = [],
        userIds = [],
        limit = 10,
        sortOrder = "desc",
        sortBy = "delayCount",
        viewMode = "top",
    } = options || {};

    const [data, setData] = useState([]);
    const [workShiftLogs, setWorkShiftLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterOptions, setFilterOptions] = useState({
        departments: [],
        shifts: [],
        employees: [], // Добавляем сотрудников в опции фильтра
    });

    // Получение опций для фильтрации
    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const response = await fetchWithAuth(
                    `${process.env.REACT_APP_API_URL}/analytics/filter-options`,
                    { method: "GET" }
                );

                const result = await response.json();

                // Проверяем ответ и корректируем структуру если нужно
                const employees = result.users || result.employees || [];

                setFilterOptions({
                    departments: result.departments || [],
                    shifts: result.shifts || [],
                    employees,
                });
            } catch (err) {
                console.error("Ошибка при загрузке опций фильтров:", err);
            }
        };

        fetchFilterOptions();
    }, []);

    // Определяем реальные значения для сортировки на основе режима просмотра
    const effectiveSortOrder = useMemo(() => {
        if (viewMode === "top") return "desc";
        if (viewMode === "bottom") return "asc";
        return sortOrder;
    }, [viewMode, sortOrder]);

    // Получение данных с учетом фильтров
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const startDate = dateRange.start.toISOString().split("T")[0];
                const endDate = dateRange.end.toISOString().split("T")[0];

                // Формируем строку запроса для иерархических данных
                let hierarchicalUrl = `${
                    process.env.REACT_APP_API_URL
                }/analytics/hierarchical?startDate=${startDate}&endDate=${endDate}&groupBy=${groupBy}&limit=${
                    viewMode === "all" ? 0 : limit
                }&sortOrder=${effectiveSortOrder}&sortBy=${
                    sortBy === "totalViolations" ? "delayCount" : sortBy
                }`;

                // Добавляем фильтры, если они заданы
                if (departmentIds && departmentIds.length > 0) {
                    departmentIds.forEach((id) => {
                        hierarchicalUrl += `&departmentIds=${id}`;
                    });
                }

                if (shiftIds && shiftIds.length > 0) {
                    shiftIds.forEach((id) => {
                        hierarchicalUrl += `&shiftIds=${id}`;
                    });
                }

                if (userIds && userIds.length > 0) {
                    userIds.forEach((id) => {
                        hierarchicalUrl += `&userIds=${id}`;
                    });
                }

                // Получение детальных данных о рабочих сменах для тепловой карты
                let logsUrl = `${process.env.REACT_APP_API_URL}/analytics/work-shift-logs?startDate=${startDate}&endDate=${endDate}`;

                if (departmentIds && departmentIds.length === 1) {
                    logsUrl += `&departmentId=${departmentIds[0]}`;
                }

                if (shiftIds && shiftIds.length === 1) {
                    logsUrl += `&shiftScheduleId=${shiftIds[0]}`;
                }

                if (userIds && userIds.length === 1) {
                    logsUrl += `&userId=${userIds[0]}`;
                }

                // Выполняем запросы параллельно для оптимизации
                const [hierarchicalResponse, logsResponse] = await Promise.all([
                    fetchWithAuth(hierarchicalUrl, { method: "GET" }),
                    fetchWithAuth(logsUrl, { method: "GET" }),
                ]);

                const hierarchicalResult = await hierarchicalResponse.json();
                const logsResult = await logsResponse.json();

                // Сохраняем данные
                setWorkShiftLogs(logsResult);

                // Добавляем агрегированную метрику totalViolations для каждого элемента
                const enrichedData = hierarchicalResult.map((item) => ({
                    ...item,
                    totalViolations:
                        (item.delayCount || 0) +
                        (item.missedCount || 0) +
                        (item.earlyExitCount || 0),
                }));

                // Если выбрана сортировка по totalViolations, сортируем данные локально
                if (sortBy === "totalViolations") {
                    enrichedData.sort((a, b) => {
                        return effectiveSortOrder === "desc"
                            ? b.totalViolations - a.totalViolations
                            : a.totalViolations - b.totalViolations;
                    });

                    // Применяем лимит после сортировки, если не выбран режим "все"
                    if (viewMode !== "all" && limit > 0) {
                        setData(enrichedData.slice(0, limit));
                    } else {
                        setData(enrichedData);
                    }
                } else {
                    // Если включен режим "все", не применяем лимит
                    if (viewMode === "all") {
                        setData(enrichedData);
                    } else if (limit > 0) {
                        setData(enrichedData.slice(0, limit));
                    } else {
                        setData(enrichedData);
                    }
                }
            } catch (err) {
                console.error("Ошибка при загрузке иерархических данных:", err);
                setError(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [
        dateRange,
        groupBy,
        departmentIds,
        shiftIds,
        userIds,
        limit,
        effectiveSortOrder,
        sortBy,
        viewMode,
    ]);

    // Данные для графика
    const chartData = useMemo(() => {
        if (!data || data.length === 0) return null;

        // 1. Сначала фильтруем только тех, у кого есть нарушения
        const filteredItems = data.filter(
            (item) =>
                (item.delayCount || 0) > 0 ||
                (item.overtimeCount || 0) > 0 ||
                (item.earlyExitCount || 0) > 0 ||
                (item.missedCount || 0) > 0
        );

        if (filteredItems.length === 0) return null; // нет данных для графика

        // 2. Создаём labels и значения
        const labels = [];
        const delayValues = [];
        const overtimeValues = [];
        const earlyExitValues = [];
        const missedValues = [];
        const totalViolationValues = [];

        filteredItems.forEach((item) => {
            let label;
            switch (groupBy) {
                case "department":
                    label = item.departmentName || "Неизвестный отдел";
                    break;
                case "shift":
                    label =
                        `${item.departmentName || ""} - ${
                            item.shiftName || ""
                        }`.trim() || "Неизвестная смена";
                    break;
                case "employee":
                    label = item.userName || "Неизвестный сотрудник";
                    break;
                default:
                    label = "Компания";
            }

            labels.push(label);
            delayValues.push(item.delayCount || 0);
            overtimeValues.push(item.overtimeCount || 0);
            earlyExitValues.push(item.earlyExitCount || 0);
            missedValues.push(item.missedCount || 0);
            totalViolationValues.push(item.totalViolations || 0);
        });

        // 3. Собираем датасеты
        const datasets = [
            {
                label: "Опоздания",
                data: delayValues,
                ...CHART_DATASETS.delays,
            },
            {
                label: "Переработки",
                data: overtimeValues,
                ...CHART_DATASETS.overtime,
            },
            {
                label: "Ранние уходы",
                data: earlyExitValues,
                ...CHART_DATASETS.earlyExit,
            },
            {
                label: "Пропуски",
                data: missedValues,
                ...CHART_DATASETS.missed,
            },
            {
                label: "Общие нарушения",
                data: totalViolationValues,
                ...CHART_DATASETS.attendance,
                hidden: sortBy !== "totalViolations",
            },
        ];

        // 4. Фильтруем пустые датасеты (нет смысла рисовать пустые графики)
        const filteredDatasets = datasets.filter(
            (ds) =>
                Array.isArray(ds.data) &&
                ds.data.some((value) => typeof value === "number" && value > 0)
        );

        return {
            labels,
            datasets: filteredDatasets,
        };
    }, [data, groupBy, sortBy]);

    //   const workedHoursData = useMemo(() => {
    // 	if (!workShiftLogs || workShiftLogs.length === 0) return [];

    // 	const map = {};

    // 	workShiftLogs.forEach(log => {
    // 	  const { user, shiftSchedule, enteredAt, exitedAt } = log;
    // 	  if (!shiftSchedule || !log.date) return; // безопасность

    // 	  const label = (() => {
    // 		if (groupBy === 'department') return user?.department?.name || 'Неизвестный отдел';
    // 		if (groupBy === 'shift') return `${user?.department?.name || ''} - ${shiftSchedule?.name || ''}`.trim();
    // 		if (groupBy === 'employee') return user?.fullName || 'Неизвестный сотрудник';
    // 		return 'Компания';
    // 	  })();

    // 	  if (!label) return;

    // 	  if (!map[label]) {
    // 		map[label] = {
    // 		  label,
    // 		  plannedMinutes: 0,
    // 		  actualMinutes: 0,
    // 		};
    // 	  }

    // 	  // Расчёт длительности смены по графику
    // 	  const [startHours, startMinutes] = shiftSchedule.startTime.split(':').map(Number);
    // 	  const [endHours, endMinutes] = shiftSchedule.endTime.split(':').map(Number);
    // 	  let plannedMinutes = (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);

    // 	  if (plannedMinutes < 0) plannedMinutes += 24 * 60; // если смена пересекает полночь
    // 	  // Вычитаем перерыв, если он указан
    //       if (shiftSchedule.breakStart && shiftSchedule.breakEnd) {
    //       	const [breakStartHours, breakStartMinutes] = shiftSchedule.breakStart.split(':').map(Number);
    //       	const [breakEndHours, breakEndMinutes] = shiftSchedule.breakEnd.split(':').map(Number);

    //       	let breakDuration = (breakEndHours * 60 + breakEndMinutes) - (breakStartHours * 60 + breakStartMinutes);
    //       	if (breakDuration < 0) breakDuration += 24 * 60; // если перерыв через полночь

    //       	plannedMinutes -= breakDuration;
    //       }

    // 	  map[label].plannedMinutes += plannedMinutes;

    // 	  // Фактическое отработанное время
    // 	  if (enteredAt && exitedAt) {
    // 		const entered = new Date(enteredAt);
    // 		const exited = new Date(exitedAt);
    // 		const actualMinutes = Math.max((exited - entered) / 60000, 0); // защита от отрицательных значений
    // 		map[label].actualMinutes += actualMinutes;
    // 	  }
    // 	});

    // 	// Конвертация в массив + перевод в часы
    // 	return Object.values(map).map(item => ({
    // 	  ...item,
    // 	  plannedHours: +(item.plannedMinutes / 60).toFixed(2),
    // 	  actualHours: +(item.actualMinutes / 60).toFixed(2),
    // 	}));
    //   }, [workShiftLogs, groupBy]);

    const workedHoursChartData = useMemo(() => {
        if (!workShiftLogs || workShiftLogs.length === 0) return null;

        const map = {};

        workShiftLogs.forEach((log) => {
            const { user, shiftSchedule, enteredAt, exitedAt } = log;
            if (!shiftSchedule || !log.date) return;

            const label = (() => {
                if (groupBy === "department")
                    return user?.department?.name || "Неизвестный отдел";
                if (groupBy === "shift")
                    return `${user?.department?.name || ""} - ${
                        shiftSchedule?.name || ""
                    }`.trim();
                if (groupBy === "employee")
                    return user?.fullName || "Неизвестный сотрудник";
                return "Компания";
            })();

            if (!label) return;

            if (!map[label]) {
                map[label] = {
                    plannedMinutes: 0,
                    actualMinutes: 0,
                    overtimeMinutes: 0,
                };
            }

            // Время начала и окончания смены по графику
            const [startHours, startMinutes] = shiftSchedule.startTime
                .split(":")
                .map(Number);
            const [endHours, endMinutes] = shiftSchedule.endTime
                .split(":")
                .map(Number);

            const shiftStart = new Date(log.date);
            shiftStart.setHours(startHours, startMinutes, 0, 0);

            const shiftEnd = new Date(log.date);
            shiftEnd.setHours(endHours, endMinutes, 0, 0);
            if (shiftEnd <= shiftStart)
                shiftEnd.setDate(shiftEnd.getDate() + 1); // смена через полночь

            let plannedMinutes = (shiftEnd - shiftStart) / 60000;

            // учёт перерыва
            if (shiftSchedule.breakStart && shiftSchedule.breakEnd) {
                const [breakStartHours, breakStartMinutes] =
                    shiftSchedule.breakStart.split(":").map(Number);
                const [breakEndHours, breakEndMinutes] = shiftSchedule.breakEnd
                    .split(":")
                    .map(Number);

                const breakStart = new Date(log.date);
                breakStart.setHours(breakStartHours, breakStartMinutes, 0, 0);

                const breakEnd = new Date(log.date);
                breakEnd.setHours(breakEndHours, breakEndMinutes, 0, 0);
                if (breakEnd <= breakStart)
                    breakEnd.setDate(breakEnd.getDate() + 1);

                plannedMinutes -= Math.max((breakEnd - breakStart) / 60000, 0);
            }

            map[label].plannedMinutes += plannedMinutes;

            if (enteredAt && exitedAt) {
                const entered = new Date(enteredAt);
                const exited = new Date(exitedAt);

                // Работал с момента начала смены до выхода (но не дольше конца смены)
                const actualEnd = exited < shiftEnd ? exited : shiftEnd;
                const workedMinutes = Math.max(
                    (actualEnd - shiftStart) / 60000,
                    0
                );

                map[label].actualMinutes += workedMinutes;

                // Переработка = то, что превышает график
                const overtimeMinutes =
                    exited > shiftEnd ? (exited - shiftEnd) / 60000 : 0;
                map[label].overtimeMinutes += overtimeMinutes;
            }
        });

        const labels = Object.keys(map);

        const plannedData = labels.map((label) => {
            const { plannedMinutes, actualMinutes } = map[label];
            return actualMinutes >= plannedMinutes
                ? plannedMinutes / 60
                : actualMinutes / 60;
        });

        const overtimeData = labels.map(
            (label) => map[label].overtimeMinutes / 60
        );

        const underworkedData = labels.map((label) => {
            const { plannedMinutes, actualMinutes } = map[label];
            return actualMinutes < plannedMinutes
                ? (plannedMinutes - actualMinutes) / 60
                : 0;
        });

        return {
            labels,
            datasets: [
                {
                    label: "Норма по графику (часы)",
                    data: plannedData,
                    backgroundColor: PRIMARY_COLORS.green.light,
                    borderColor: PRIMARY_COLORS.green.main,
                    borderWidth: 1,
                    stack: "worked",
                },
                {
                    label: "Переработка (часы)",
                    data: overtimeData,
                    backgroundColor: PRIMARY_COLORS.indigo.light,
                    borderColor: PRIMARY_COLORS.indigo.main,
                    borderWidth: 1,
                    stack: "worked",
                },
                {
                    label: "Недоработка (часы)",
                    data: underworkedData,
                    backgroundColor: PRIMARY_COLORS.red.light,
                    borderColor: PRIMARY_COLORS.red.main,
                    borderWidth: 1,
                    stack: "missed",
                },
            ],
        };
    }, [workShiftLogs, groupBy]);

    const heatmapChartData = useMemo(() => {
        if (!workShiftLogs || workShiftLogs.length === 0) return null;

        const map = {}; // карта для агрегации
        const yLabelsSet = new Set();

        workShiftLogs.forEach((log) => {
            const shiftSchedule = log.shiftSchedule;
            const metrics = analyzeWorkShift(log, shiftSchedule);
            const date = new Date(log.date);

            let yLabel = "Компания";
            if (groupBy === "department") {
                yLabel = log.user?.department?.name || "Неизвестный отдел";
            } else if (groupBy === "shift") {
                yLabel = `${log.user?.department?.name || ""} - ${
                    shiftSchedule?.name || ""
                }`.trim();
            } else if (groupBy === "employee") {
                yLabel = log.user?.fullName || "Неизвестный сотрудник";
            }

            const key = `${date.toISOString().split("T")[0]}_${yLabel}`; // ключ: дата + label

            if (!map[key]) {
                map[key] = {
                    x: date,
                    y: yLabel,
                    delay: 0,
                    overtime: 0,
                    earlyExit: 0,
                    missed: false,
                };
            }

            map[key].delay += metrics?.delayMinutes || 0;
            map[key].overtime += metrics?.overtimeMinutes || 0;
            map[key].earlyExit += metrics?.earlyExitMinutes || 0;

            // Если хотя бы один лог пропущен — считаем всю точку пропущенной
            if (!log.enteredAt && !log.exitedAt) {
                map[key].missed = true;
            }

            yLabelsSet.add(yLabel);
        });

        const points = Object.values(map); // превращаем карту обратно в массив
        const labels = Array.from(yLabelsSet).sort((a, b) =>
            a.localeCompare(b, "ru")
        );

        return {
            points,
            labels,
            groupBy, // передаём явно
        };
    }, [workShiftLogs, groupBy]);

    // Формируем данные хлебных крошек
    const breadcrumbData = useMemo(() => {
        const breadcrumbs = [{ id: "company", label: "Предприятие", level: 0 }];

        // Если выбран отдел
        if (
            groupBy === "department" ||
            (departmentIds && departmentIds.length > 0)
        ) {
            if (departmentIds && departmentIds.length === 1) {
                const department = filterOptions?.departments?.find(
                    (d) => d._id === departmentIds[0]
                );
                if (department) {
                    breadcrumbs.push({
                        id: department._id,
                        label: department.name,
                        level: 1,
                        type: "department",
                    });
                }
            } else if (departmentIds && departmentIds.length > 1) {
                breadcrumbs.push({
                    id: "multiple-departments",
                    label: `Выбрано отделов: ${departmentIds.length}`,
                    level: 1,
                    type: "department",
                });
            } else {
                breadcrumbs.push({
                    id: "all-departments",
                    label: "Все отделы",
                    level: 1,
                    type: "department",
                });
            }
        }

        // Если выбрана смена
        if (groupBy === "shift" || (shiftIds && shiftIds.length > 0)) {
            if (shiftIds && shiftIds.length === 1) {
                const shift = filterOptions?.shifts?.find(
                    (s) => s._id === shiftIds[0]
                );
                if (shift) {
                    breadcrumbs.push({
                        id: shift._id,
                        label: shift.name,
                        level: 2,
                        type: "shift",
                        departmentId:
                            departmentIds && departmentIds.length === 1
                                ? departmentIds[0]
                                : null,
                    });
                }
            } else if (shiftIds && shiftIds.length > 1) {
                breadcrumbs.push({
                    id: "multiple-shifts",
                    label: `Выбрано смен: ${shiftIds.length}`,
                    level: 2,
                    type: "shift",
                });
            } else {
                breadcrumbs.push({
                    id: "all-shifts",
                    label: "Все смены",
                    level: 2,
                    type: "shift",
                });
            }
        }

        // Если выбраны сотрудники
        if (groupBy === "employee" || (userIds && userIds.length > 0)) {
            if (userIds && userIds.length === 1) {
                const employee = filterOptions?.employees?.find(
                    (e) => e._id === userIds[0]
                );
                if (employee) {
                    breadcrumbs.push({
                        id: employee._id,
                        label: `${employee.lastName} ${employee.firstName}`,
                        level: 3,
                        type: "employee",
                        departmentId:
                            departmentIds && departmentIds.length === 1
                                ? departmentIds[0]
                                : null,
                        shiftId:
                            shiftIds && shiftIds.length === 1
                                ? shiftIds[0]
                                : null,
                    });
                }
            } else if (userIds && userIds.length > 1) {
                breadcrumbs.push({
                    id: "multiple-employees",
                    label: `Выбрано сотрудников: ${userIds.length}`,
                    level: 3,
                    type: "employee",
                });
            } else {
                breadcrumbs.push({
                    id: "all-employees",
                    label: "Все сотрудники",
                    level: 3,
                    type: "employee",
                });
            }
        }

        return breadcrumbs;
    }, [groupBy, departmentIds, shiftIds, userIds, filterOptions]);

    return {
        data,
        chartData,
        heatmapChartData,
        isLoading,
        error,
        filterOptions,
        // workedHoursData,
        workedHoursChartData,
        breadcrumbData,
    };
};
